import { parse } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Loader, Modal, TableRow as Row, Table, TableBody, TableHeader, TableHeaderCell } from "semantic-ui-react";

import { DATE_FORMATS_FNS, displayDate, useRdwDetailsQuery } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { createFormHelper } from "lib/Form";
import { usePonOvis } from "modules/AppointmentDetails/components/PonOvis/hooks/usePonOvis";
import "modules/Appointments/components/NewAppointment/components/CarSearchFormSection/components/CarExternalDataModal/CarExternalDataModal.scss";
import {
  CheckboxTableHeaderCell,
  TableRow
} from "modules/Appointments/components/NewAppointment/components/CarSearchFormSection/components/CarExternalDataModal/components";
import { useContractROBDataQuery } from "modules/Appointments/components/NewAppointment/hooks";
import { formatRegistrationNumber } from "modules/Appointments/components/NewAppointment/utils";
import { formatDate } from "utils/dateHelperFunctions";

import type { AppointmentSection, CarSection } from "modules/Appointments/components/NewAppointment";

type CarExternalDataModalProps = {
  open: boolean;
  carFormValues: CarSection;
  carSearchTerm: string;
  handleSetCarFromExternalData: (value: boolean) => void;
  onClose: () => void;
};

enum DATA_SOURCE_TYPE {
  RDW = "rdw",
  ROB = "rob",
  OVIS = "ovis"
}

type SelectedFields = {
  make: { type: DATA_SOURCE_TYPE.RDW | DATA_SOURCE_TYPE.ROB | null; value: string };
  model: { type: DATA_SOURCE_TYPE.RDW | DATA_SOURCE_TYPE.ROB | null; value: string };
  apk: { type: DATA_SOURCE_TYPE.RDW | DATA_SOURCE_TYPE.OVIS | null; value: string };
  license: { type: DATA_SOURCE_TYPE.OVIS | null; value: string };
  vin: { type: DATA_SOURCE_TYPE.OVIS | null; value: string };
};

const FormHelper = createFormHelper<AppointmentSection>();

export const CarExternalDataModal = ({ open, carFormValues, carSearchTerm, handleSetCarFromExternalData, onClose }: CarExternalDataModalProps) => {
  const { t } = useTranslation();

  const { setValue } = FormHelper.useFormContext();

  const { selectedLocation } = useSelectedLocation();
  const regNr = carFormValues.reg_nr || carSearchTerm;
  const { rdwDetails: rdwData, fetchRdwDetails, isRdwFetching: isRdwLoading } = useRdwDetailsQuery({ regNumber: regNr, country: selectedLocation?.country });
  const { data: robData, refetch: refetchRobData, isFetching: isFetchingRobData } = useContractROBDataQuery(regNr, selectedLocation?.id);
  const {
    data: ponOvisData,
    refetch: refetchOvisData,
    isFetching: isLoadingPonOvis
  } = usePonOvis({ isModalOpen: open, car_id: carFormValues.id, vin_nr: carFormValues.vin_nr, dealer_location_id: selectedLocation?.id });
  const [selectedFields, setSelectedFields] = useState<SelectedFields>({
    make: { type: null, value: "" },
    model: { type: null, value: "" },
    apk: { type: null, value: "" },
    license: { type: null, value: "" },
    vin: { type: null, value: "" }
  });
  const [lastCarReg, setLastCarReg] = useState<string | undefined>(carFormValues.reg_nr);
  const [hasSaved, setHasSaved] = useState(false);

  const ovisEventType = ponOvisData?.find(item => item.eventTypeCode === "AM");
  const hasRobData = robData && (robData.vehicle.vehicleMake || robData.vehicle.vehicleModel);
  const hasRdwData = Boolean(rdwData?.merk || rdwData?.handelsbenaming || rdwData?.vervaldatum_apk);
  const hasOvisData = Boolean(ovisEventType?.eventDate || ovisEventType?.licensePlate);
  // TODO: Temporarily disabled in CLA-I10825 till we find correct solution to get ovis data without having to pass vin nr
  // Using Boolean(false) to work around the no-unnecessary-condition errors from eslint
  const isPonEnabled = Boolean(false); // selectedDealer?.is_pon_enabled
  const isRobNetEnabled = selectedLocation?.is_robnet_enabled;
  const hasAnyData = hasRdwData || (isRobNetEnabled && hasRobData) || (isPonEnabled && hasOvisData);

  const isLoading = isRdwLoading || isFetchingRobData || isLoadingPonOvis;

  const isSelectAllOfType = (type: DATA_SOURCE_TYPE, keys: (keyof typeof selectedFields)[]) => {
    return keys.every(key => selectedFields[key].type === type);
  };

  const isSelectAllRDW = isSelectAllOfType(DATA_SOURCE_TYPE.RDW, ["make", "model", "apk"]);
  const isSelectAllROB = isSelectAllOfType(DATA_SOURCE_TYPE.ROB, ["make", "model"]);
  const isSelectAllOVIS = isSelectAllOfType(DATA_SOURCE_TYPE.OVIS, ["apk", "license", "vin"]);

  const toggleField = (key: keyof typeof selectedFields, type: DATA_SOURCE_TYPE) => {
    setSelectedFields(prev => {
      if (prev[key].type === type) return { ...prev, [key]: { type: null, value: "" } };

      let value = "";
      if (type === DATA_SOURCE_TYPE.RDW) {
        if (key === "make") value = rdwData?.merk || "";
        if (key === "model") value = rdwData?.handelsbenaming || "";
        if (key === "apk") value = rdwData?.vervaldatum_apk ? displayDate(rdwData.vervaldatum_apk) : "";
      } else if (type === DATA_SOURCE_TYPE.ROB) {
        if (key === "make") value = robData?.vehicle.vehicleMake || "";
        if (key === "model") value = robData?.vehicle.vehicleModel || "";
      } else {
        if (key === "apk") value = ovisEventType?.eventDate || "";
        if (key === "license") value = ovisEventType?.licensePlate || "";
        if (key === "vin") value = ovisEventType?.vin || "";
      }

      return { ...prev, [key]: { type, value } };
    });
  };

  const toggleSelectAll = (type: DATA_SOURCE_TYPE) => {
    setSelectedFields(prev => {
      let isAllSelected: boolean;

      if (type === DATA_SOURCE_TYPE.RDW) {
        isAllSelected = isSelectAllRDW;
      } else if (type === DATA_SOURCE_TYPE.ROB) {
        isAllSelected = isSelectAllROB;
      } else {
        isAllSelected = isSelectAllOVIS;
      }

      if (isAllSelected) {
        return {
          make: prev.make.type === type ? { type: null, value: "" } : prev.make,
          model: prev.model.type === type ? { type: null, value: "" } : prev.model,
          apk: prev.apk.type === type ? { type: null, value: "" } : prev.apk,
          license: prev.license.type === type ? { type: null, value: "" } : prev.license,
          vin: prev.vin.type ? { type: null, value: "" } : prev.license
        };
      } else {
        if (type === DATA_SOURCE_TYPE.RDW) {
          return {
            ...prev,
            make: { type, value: rdwData?.merk || "" },
            model: { type, value: rdwData?.handelsbenaming || "" },
            apk: { type, value: rdwData?.vervaldatum_apk ? displayDate(rdwData.vervaldatum_apk) : "" }
          };
        }

        if (type === DATA_SOURCE_TYPE.ROB) {
          return {
            ...prev,
            make: { type, value: robData?.vehicle.vehicleMake || "" },
            model: { type, value: robData?.vehicle.vehicleModel || "" },
            apk: prev.apk
          };
        }

        return {
          ...prev,
          apk: { type, value: ovisEventType?.eventDate || "" },
          license: { type, value: ovisEventType?.licensePlate || "" },
          vin: { type, value: ovisEventType?.vin || "" }
        };
      }
    });
  };

  const hasOvisParams = (carFormValues.id && carFormValues.id > 0) || (Boolean(carFormValues.vin_nr) && Boolean(selectedLocation?.id));

  useEffect(() => {
    if (open) {
      fetchRdwDetails();
      if (isRobNetEnabled) refetchRobData();
      if (hasOvisParams) refetchOvisData();
    }
  }, [open, regNr, hasOvisParams, isRobNetEnabled]);

  useEffect(() => {
    if (regNr !== lastCarReg) {
      setSelectedFields({
        make: { type: null, value: "" },
        model: { type: null, value: "" },
        apk: { type: null, value: "" },
        license: { type: null, value: "" },
        vin: { type: null, value: "" }
      });
      setLastCarReg(regNr);
      setHasSaved(false);
    }
  }, [regNr, lastCarReg, hasSaved]);

  const handleCancel = () => {
    if (!hasSaved)
      setSelectedFields({
        make: { type: null, value: "" },
        model: { type: null, value: "" },
        apk: { type: null, value: "" },
        license: { type: null, value: "" },
        vin: { type: null, value: "" }
      });
    onClose();
  };

  const handleSave = () => {
    if (!carFormValues.reg_nr && regNr) setValue("car.reg_nr", regNr);
    if (selectedFields.make.type) setValue("car.make", selectedFields.make.value);
    if (selectedFields.model.type) setValue("car.model", selectedFields.model.value);

    if (selectedFields.apk.type === DATA_SOURCE_TYPE.RDW && selectedFields.apk.value)
      setValue("car.apk_date_dms", parse(selectedFields.apk.value, DATE_FORMATS_FNS.dateMonthYear, new Date()).toISOString());

    if (selectedFields.apk.type === DATA_SOURCE_TYPE.OVIS && selectedFields.apk.value) setValue("car.apk_date_dms", formatDate(selectedFields.apk.value));
    if (selectedFields.license.type === DATA_SOURCE_TYPE.OVIS && selectedFields.license.value)
      setValue("car.reg_nr", formatRegistrationNumber(selectedFields.license.value));
    if (selectedFields.vin.type === DATA_SOURCE_TYPE.OVIS && selectedFields.vin.value) setValue("car.vin_nr", selectedFields.vin.value);

    handleSetCarFromExternalData(true);
    setHasSaved(true);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} size="large" className="CarExternalDataModal">
      <Modal.Header>{t("v8_car_external_import", "Car - External Import")}</Modal.Header>
      <Modal.Content className="modal-content">
        {isLoading && <Loader active inline="centered" inverted className="Loader" />}
        {!isLoading && !hasAnyData && <p className="no-data">{t("v8_no_data", "No data")}</p>}

        {!isLoading && hasAnyData && (
          <Table basic="very" fixed>
            <TableHeader>
              <Row>
                <TableHeaderCell>{t("v8_field", "Field")}</TableHeaderCell>
                <TableHeaderCell>{t("v8_current_value", "Current value")}</TableHeaderCell>
                <CheckboxTableHeaderCell label="RDW" checked={isSelectAllRDW} onToggle={() => toggleSelectAll(DATA_SOURCE_TYPE.RDW)} disabled={!rdwData} />
                {isRobNetEnabled && (
                  <CheckboxTableHeaderCell label="ROB" checked={isSelectAllROB} onToggle={() => toggleSelectAll(DATA_SOURCE_TYPE.ROB)} disabled={!hasRobData} />
                )}
                {isPonEnabled && (
                  <CheckboxTableHeaderCell label="OVIS" checked={isSelectAllOVIS} onToggle={() => toggleSelectAll(DATA_SOURCE_TYPE.OVIS)} disabled={!ovisEventType} />
                )}
              </Row>
            </TableHeader>

            <TableBody>
              <TableRow
                label={t("v8_make", "Make")}
                currentValue={carFormValues.make}
                rows={[
                  {
                    value: rdwData?.merk,
                    checked: selectedFields.make.type === DATA_SOURCE_TYPE.RDW,
                    onToggle: () => toggleField("make", DATA_SOURCE_TYPE.RDW)
                  },
                  ...(isRobNetEnabled
                    ? [
                        {
                          value: robData?.vehicle.vehicleMake,
                          checked: selectedFields.make.type === DATA_SOURCE_TYPE.ROB,
                          onToggle: () => toggleField("make", DATA_SOURCE_TYPE.ROB),
                          disabled: !hasRobData
                        }
                      ]
                    : []),
                  ...(isPonEnabled ? [{ value: null }] : [])
                ]}
              />

              <TableRow
                label={t("v8_model", "Model")}
                currentValue={carFormValues.model}
                rows={[
                  {
                    value: rdwData?.handelsbenaming,
                    checked: selectedFields.model.type === DATA_SOURCE_TYPE.RDW,
                    onToggle: () => toggleField("model", DATA_SOURCE_TYPE.RDW)
                  },
                  ...(isRobNetEnabled
                    ? [
                        {
                          value: robData?.vehicle.vehicleModel,
                          checked: selectedFields.model.type === DATA_SOURCE_TYPE.ROB,
                          onToggle: () => toggleField("model", DATA_SOURCE_TYPE.ROB),
                          disabled: !hasRobData
                        }
                      ]
                    : []),
                  ...(isPonEnabled ? [{ value: null }] : [])
                ]}
              />

              <TableRow
                label={t("v8_apk_date", "APK date")}
                currentValue={carFormValues.apk_date_dms ? formatDate(carFormValues.apk_date_dms, DATE_FORMATS_FNS.dateMonthYear) : ""}
                rows={[
                  {
                    value: rdwData?.vervaldatum_apk && displayDate(rdwData.vervaldatum_apk),
                    checked: selectedFields.apk.type === DATA_SOURCE_TYPE.RDW,
                    onToggle: () => toggleField("apk", DATA_SOURCE_TYPE.RDW)
                  },
                  ...(isRobNetEnabled ? [{ value: null }] : []),
                  ...(isPonEnabled
                    ? [
                        {
                          value: ovisEventType?.eventDate && formatDate(ovisEventType.eventDate, DATE_FORMATS_FNS.dateMonthYear),
                          checked: selectedFields.apk.type === DATA_SOURCE_TYPE.OVIS,
                          onToggle: () => toggleField("apk", DATA_SOURCE_TYPE.OVIS)
                        }
                      ]
                    : [])
                ]}
              />

              <TableRow
                label={t("v8_license", "License")}
                currentValue={carFormValues.reg_nr}
                rows={[
                  { value: null },
                  ...(isRobNetEnabled ? [{ value: null }] : []),
                  ...(isPonEnabled
                    ? [
                        {
                          value: ovisEventType?.licensePlate && formatRegistrationNumber(ovisEventType.licensePlate),
                          checked: selectedFields.license.type === DATA_SOURCE_TYPE.OVIS,
                          onToggle: () => toggleField("license", DATA_SOURCE_TYPE.OVIS)
                        }
                      ]
                    : [])
                ]}
              />

              <TableRow
                label={t("v8_vin_number", "Vin #")}
                currentValue={carFormValues.vin_nr}
                rows={[
                  { value: null },
                  ...(isRobNetEnabled ? [{ value: null }] : []),
                  ...(isPonEnabled
                    ? [
                        {
                          value: ovisEventType?.vin && ovisEventType.vin,
                          checked: selectedFields.vin.type === DATA_SOURCE_TYPE.OVIS,
                          onToggle: () => toggleField("vin", DATA_SOURCE_TYPE.OVIS)
                        }
                      ]
                    : [])
                ]}
              />
            </TableBody>
          </Table>
        )}
      </Modal.Content>
      <Modal.Actions className="modal-actions">
        <Button className="light" onClick={handleCancel} type="reset">
          {t("v8_cancel", "Cancel")}
          <Icon className="xmark" />
        </Button>
        <Button color="green" onClick={handleSave} disabled={!hasAnyData}>
          {t("v8_update", "Update")}
          <Icon className="check" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
